import { SESSION_STORAGE_KEYS } from "@js/constants/constants-imports.es6.js";
import { LuminisServiceUtils, SessionStorageUtils } from "@js/utils/utils-imports.es6.js";
import ApiErrorHandler from "@js/errors/api-error-handler.js";
import LoginPage from "@js/mychart/login.es6.js";
import PostLoginRedirectUtils from "@js/mychart/post-login-redirect-utils.es6.js";

export const AUTHENTICATION_STATUS = {
  AUTHENTICATION_SUCCEEDED: 'AuthenticationSucceeded',
  AUTHENTICATION_FAILED: 'AuthenticationFailed',
  PASSWORD_EXPIRED: 'PasswordExpired',
};

export default class MyChartUser {
  constructor(patientId, myChartId, username, fullName) {
    this.patientId = patientId;
    this.myChartId = myChartId;
    this.username = username;
    this.firstName = fullName.split(' ')[0];
    this.fullName = fullName;
  }

  static async getLoggedInUser() {
    const luminisServiceUtils = new LuminisServiceUtils();

    let userJson = await luminisServiceUtils.getLoggedInUser()

    // user is logged out
    if (userJson?.statusCode === "Unauthorized") {
      // TODO LH-3198 support third party cookies.
      // expected failure, return null
      return null;
    } else if (userJson) {
      userJson = userJson.result;
    } else {
      let statusCode = '';
      let statusText = '';
      if (userJson) {
        statusCode = userJson.statusCode;
        statusText = userJson.statusText;
      }
      // throw our error here, its none of our expected errors.
      ApiErrorHandler.handleError(statusCode, statusText,
        '', '', '', 'Auth check threw unexpected error', null)
    }

    return new MyChartUser(
      userJson.patientId,
      userJson.myChartId,
      userJson.myChartUsername,
      userJson.fullName,
    );
  }

  /**
   * If the patient enters invalid credentials, the following redirects occur:
   * https://luminisstg.luminishealth.org/en/login
   * https://mychartnp.aahs.org/MyChartPOC/Authentication/Login/DoLogin
   * https://luminisstg.luminishealth.org/en/login?&errorNamespace=AuthError&errorKey=loginfailed
   *
   * Information such as query string parameters and the referrer are lost
   * when these redirects occur.  We store the previously calculated resume
   * URL so that we can send them back to it when they do enter valid
   * credentials after a bad attempt.
   */
  static storeLoginResumeUrl(resumeUrl) {
    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.LOGIN_RESUME_URL, resumeUrl);
  }

  /**
   * Determines the URL the patient should be sent to directly after a
   * successful login attempt.  This will be sent as the "resumeUrl" parameter
   * of the OAuth login endpoint.
   *
   * Use the following hierarchy:
   * (1) current page (if not currently on the login page, ie. login modal)
   * (2) "nextUrl" query string parameter
   * (3) "postloginurl" query string parameter (indicates user was sent from MyChart)
   * (4) "postloginmode" query string parameter (indicates user was sent from MyChart)
   * (5) document.referrer (only if in an approved domain, ie. current domain or MyChart)
   * (6) "loginResumeUrl" session storage value
   * (7) "workflow=mychart" query string parameter (indicates user was sent from MyChart)
   * (8) default post login page (likely the patient dashboard)
   * (9) home page
   */
  static getLoginResumeUrl() {
    const isOnLoginPage = LoginPage.isOnLoginPage();
    if (!isOnLoginPage) {
      return window.location.href;
    }

    const nextUrl = PostLoginRedirectUtils.getNextUrlFromQueryString();
    if (nextUrl) {
      return PostLoginRedirectUtils.getCustomRedirect(nextUrl);
    }

    const postLoginUrl = PostLoginRedirectUtils.getPostLoginUrlFromQueryString();
    if (postLoginUrl) {
      return PostLoginRedirectUtils.getCustomRedirect(postLoginUrl);
    }

    const postLoginMode = PostLoginRedirectUtils.getPostLoginModeUrlFromQueryString();
    if (postLoginMode) {
      return postLoginMode;
    }

    let referrerUrl = PostLoginRedirectUtils.getReferrerUrl();
    if (referrerUrl) {
      return PostLoginRedirectUtils.getCustomRedirect(referrerUrl);
    }

    const resumeUrl = PostLoginRedirectUtils.getLoginResumeUrlFromSessionStorage();
    if (resumeUrl) {
      return PostLoginRedirectUtils.getCustomRedirect(resumeUrl);
    }

    const myChartDefaultWorkflow = PostLoginRedirectUtils.getMyChartHomePageUrl();
    if (myChartDefaultWorkflow) {
      return myChartDefaultWorkflow;
    }

    const defaultPostLoginPage = PostLoginRedirectUtils.getDefaultPostLoginPageUrl();
    if (defaultPostLoginPage) {
      return defaultPostLoginPage;
    }

    return '/';
  }

  /**
   * Logs the patient out.
   *
   * successful login attempt (optional)
   */
  static async logout() {
    const luminisServiceUtils = new LuminisServiceUtils();
    await luminisServiceUtils.logout();
  }
}

(() => {})(Drupal);
